if(!customElements.get("results-list")) {
  class ResultsList extends HTMLElement {
    constructor() {
      super();
      this.productGrid = this.querySelector('#product-grid');
      this.button = this.querySelector('button[name="load-more"]');
      this.currentPage = parseInt(this.querySelector(".pagination-details")?.dataset.currentPage);  
      this.totalPages = parseInt(this.querySelector(".pagination-details")?.dataset.totalPages);

      this.basePage = parseInt(this.querySelector(".pagination-details")?.dataset.currentPage);
      this.lowestPageLoaded = this.currentPage;

      this.viewMorePrevious = this.querySelector("span#viewMorePrevious");
      this.viewMoreNext = this.querySelector("span#viewMoreNext");

      this.cachedData = null;
      this.prefetchPromise = null;
      this.isAppending = false;
      this.isFetchingPrevious = false;
     
      this.infinityScrollObserver = null;

      // One delegated listener: the Load more area (and its button) is replaced after every load.
      this.addEventListener('click', (event) => {
        if(event.target.closest('button[name="load-more"]')) this.onLoadMoreClicked();
      });
    }
    connectedCallback() {
      this.#observeViewMore();     
    }
    #observeViewMore() {
      this.infinityScrollObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target === this.viewMorePrevious) {
              // console.log("viewMorePrevious is in viewport");
              this.loadPreviousProducts();
            }
            else if (entry.target === this.viewMoreNext) {
              // console.log("viewMoreNext is in viewport");
              this.loadNextProducts();
            }            
          }
        }
      },
      {
        rootMargin: '150px',
      });
      if (this.viewMorePrevious) {
        this.infinityScrollObserver.observe(this.viewMorePrevious);
      }
      if (this.viewMoreNext) {
        this.infinityScrollObserver.observe(this.viewMoreNext);
      }    
    }
    fetchPage(url) {
      // Return a Promise that resolves to an object
      return fetch(`${url}`)
        .then(resp => {
          if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
          return resp.text();
        });
    }
    loadPreviousProducts() {
      // If we originally loaded page 1 from the server, there is no "earlier" page missing. Do nothing.
      if (this.basePage === 1) {
        // We started at page 1; user may have loaded more, but all earlier pages are already in DOM.
        return;
      }
      // Avoid overlapping requests
      if (this.isFetchingPrevious) return;
     
      // Decide which page to fetch
      const targetPage = this.lowestPageLoaded - 1;

      // If we've already reached page 1, stop
      if (targetPage < 1) {
        return;
      }      
     
      this.isFetchingPrevious = true;
      const previousProductsLoader = document.querySelector(".previous-products-loading");
      previousProductsLoader.classList.remove("hidden");      
      let previousPageUrl;

      if(this.querySelector(".pagination-details").hasAttribute("data-previous-page-url")) {
        previousPageUrl = this.querySelector(".pagination-details")?.dataset.previousPageUrl;
        // console.log("Fetching previous page:", previousPageUrl);
        fetch(previousPageUrl)
        .then(res => res.text())
        .then(data => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, 'text/html');
          // Extract previous page items
          const previousProducts = doc.querySelectorAll('#product-grid .plp-grid__item');

          const grid = this.productGrid;
          if (!grid) return;

          // Store current scroll and the absolute top of the first element
          const scrollTop = window.scrollY;
          const firstElement = grid.querySelector(".plp-grid__item:first-of-type");
          const oldTop = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

          // PREPEND items
          this.productGrid.prepend(...previousProducts);
          if (typeof window.gridItemsClicked === 'function') {
            window.gridItemsClicked();
          }
          document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

          // Recalculate anchor position and adjust scroll so the view stays stable
          if (firstElement) {
            const newTop = firstElement.getBoundingClientRect().top + window.scrollY;
            const heightDiff = newTop - oldTop;

            window.scrollTo({
              top: scrollTop + heightDiff,
              // 'instant' is what Horizon uses; browsers treat unknown as 'auto'
              behavior: 'instant',
            });
          }

          this.lowestPageLoaded = targetPage;
          const updatedPaginationDetails = doc.querySelector('.pagination-details');
          const paginationDetails = document.querySelector(".pagination-details");
          paginationDetails.setAttribute("data-current-page", `${updatedPaginationDetails.getAttribute("data-current-page")}`);
          if(paginationDetails.hasAttribute("data-previous-page-url")) {
            paginationDetails.setAttribute("data-previous-page-url", `${updatedPaginationDetails.getAttribute("data-previous-page-url")}`);
          }
          
        })
        .catch((err) => {
          console.error("Error fetching previous page:", err);
        })
        .finally(() => {
          this.isFetchingPrevious = false;
          previousProductsLoader.classList.add("hidden");
        });
      }
      else {
        // console.log("previousPageUrl:", previousPageUrl);
      }

    }
    loadNextProducts() {
      if (this.currentPage >= this.totalPages) return;      
      if (this.cachedData || this.prefetchPromise) return; // already have or loading
      let nextPageUrl = this.querySelector(".pagination-details").dataset.nextPageUrl;
      if (!nextPageUrl) {
        const nextPage = this.currentPage + 1;
        nextPageUrl = `${window.location.pathname}${window.location.search ? window.location.search + '&' : '?'}page=${nextPage}`;
      }
      // console.log("nextPageUrl:", nextPageUrl);
      this.prefetchReady = false;
      this.prefetchPromise = this.fetchPage(nextPageUrl)
        .then(data => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, 'text/html');
          const newBatchOfProducts = doc.querySelectorAll('#product-grid .plp-grid__item');  
          this.cachedData = doc; // keep it until user clicks         
          return data;
        })
        .catch(err => {
          console.error('Prefetch error:', err);
          this.prefetchPromise = null;
          // do not set cachedData; let click-triggered fetch handle it later
        })
        .finally(() => {
            this.prefetchPromise = null;
        });;
    }
    async onLoadMoreClicked() {
      if (this.isAppending) return; // ignore double clicks
      this.button = this.querySelector('button[name="load-more"]');
      if (!this.button) return;      
      this.isAppending = true;
      this.button.disabled = true;
      const originalText = this.button.textContent;
      this.button.textContent = 'Loading...';
      try {
        let html;
        if (this.prefetchPromise) {
          // Wait for prefetch to finish
          let data = await this.prefetchPromise;
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, 'text/html');
          const newBatchOfProducts = doc.querySelectorAll('#product-grid .plp-grid__item');  
          html = newBatchOfProducts;
          // Update state and button
          this.currentPage += 1;          
        }
        else {
          html = this.cachedData.querySelectorAll('#product-grid .plp-grid__item'); // Use prefetched result
          // Update state and button
          this.currentPage += 1;
        }
        this.productGrid.append(...html);
        document.querySelector('.next-products-loading-method').innerHTML = this.cachedData.querySelector('.next-products-loading-method').innerHTML;
        document.querySelector('.pagination-details').replaceWith(this.cachedData.querySelector('.pagination-details'));
        const currentUrl = window.location.href;
        const url = new URL(currentUrl);
        if(!url.searchParams.has('page')) {
          url.searchParams.append('page', `${this.currentPage}`);
        }
        else {
          url.searchParams.set('page', `${this.currentPage}`);
        }        
        history.pushState({}, '', url.toString());
        this.cachedData = null;   // ensure cleared
        gridItemsClicked();
        document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

      }
      catch(err) {
        console.error('Load more error:', err);
      }
      finally {
        this.button.disabled = false;
        this.button.textContent = originalText;
        this.isAppending = false;
        this.prefetchReady = true;       
      }    
     
    }
    disconnectedCallback() {
      this.infinityScrollObserver.disconnect();
    }

  }
  customElements.define("results-list", ResultsList);
}